import { Helmet } from 'react-helmet-async';

const SITE_NAME = import.meta.env.VITE_SITE_NAME || 'Cripto News';
const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://criptonews.site';
const CONTACT_EMAIL = 'contact@criptonews.site';
const RESPONSIBLE_NAME = 'Gabriel Leite Moreira';
const RESPONSIBLE_LOCATION = 'Blumenau, SC, Brasil';
const LAST_UPDATED = '9 de maio de 2026';

export const TermsOfUsePage: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Termos de Uso | {SITE_NAME}</title>
        <meta
          name="description"
          content={`Termos de Uso do ${SITE_NAME}. Conheça as condições de utilização do nosso portal de notícias sobre criptomoedas.`}
        />
        <link rel="canonical" href={`${SITE_URL}/termos-de-uso`} />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-10 max-w-3xl">
          <div className="bg-white rounded-lg shadow p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Termos de Uso</h1>
            <p className="text-sm text-gray-500 mb-8">Última atualização: {LAST_UPDATED}</p>

            <p className="text-gray-700 mb-6">
              Bem-vindo ao <strong>{SITE_NAME}</strong> ("<a href={SITE_URL} className="text-blue-600 hover:underline">{SITE_URL}</a>").
              Ao acessar e utilizar este site, você concorda com os presentes Termos de Uso. Leia-os com atenção antes
              de utilizar nossos serviços.
            </p>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">1. Descrição do Serviço</h2>
              <p className="text-gray-700 mb-3">
                O <strong>{SITE_NAME}</strong> é um portal de curadoria e agregação de notícias sobre criptomoedas
                em português, destinado ao público brasileiro. Nosso serviço consiste em:
              </p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Monitoramento automático de publicações internacionais de criptomoedas;</li>
                <li>Geração de resumos em português para facilitar o acesso à informação;</li>
                <li>Disponibilização de ferramentas informativas como o Monitor de Arbitragem e o Glossário Cripto;</li>
                <li>Exibição de preços de criptoativos em tempo real para fins informativos.</li>
              </ul>
              <p className="text-gray-700 mt-3">
                O conteúdo original dos artigos pertence às suas respectivas fontes (CoinDesk, CoinTelegraph,
                Bitcoin.com, CryptoSlate). O {SITE_NAME} não reivindica propriedade sobre esses conteúdos.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">2. Isenção de Responsabilidade Financeira</h2>
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-3">
                <p className="text-yellow-800 font-semibold mb-2">⚠️ Aviso Importante</p>
                <p className="text-yellow-700 text-sm">
                  O {SITE_NAME} é um serviço de informação jornalística e <strong>não fornece aconselhamento
                  financeiro, de investimento ou consultoria de qualquer natureza</strong>.
                </p>
              </div>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>O conteúdo publicado é meramente informativo e não constitui recomendação de compra, venda ou
                  manutenção de qualquer criptoativo ou instrumento financeiro;</li>
                <li>Investimentos em criptomoedas envolvem alto risco e volatilidade extrema. O valor dos ativos
                  pode oscilar significativamente ou cair a zero;</li>
                <li>Nunca invista mais do que pode perder;</li>
                <li>Consulte sempre um profissional financeiro qualificado antes de tomar qualquer decisão de
                  investimento;</li>
                <li>O {SITE_NAME} não se responsabiliza por perdas financeiras decorrentes do uso das informações
                  disponibilizadas neste site.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">3. Propriedade Intelectual</h2>
              <p className="text-gray-700 mb-3">
                Os resumos em português, a curadoria editorial, o design, os textos originais e o código-fonte
                do {SITE_NAME} são de propriedade de {RESPONSIBLE_NAME} e protegidos pela legislação brasileira
                de propriedade intelectual (Lei nº 9.610/98).
              </p>
              <p className="text-gray-700 mb-3">
                O conteúdo dos artigos agregados pertence às suas respectivas fontes originais. Todos os artigos
                incluem link direto para a fonte original, em respeito à propriedade intelectual dos autores.
              </p>
              <p className="text-gray-700">
                É vedada a reprodução, cópia ou distribuição do conteúdo deste site sem autorização prévia e
                expressa, exceto para uso pessoal e não comercial, desde que citada a fonte.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">4. Limitação de Responsabilidade</h2>
              <p className="text-gray-700 mb-3">O {SITE_NAME} não se responsabiliza por:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Imprecisões, erros ou desatualizações nas informações publicadas pelas fontes originais;</li>
                <li>Disponibilidade ininterrupta do serviço, podendo ocorrer interrupções por manutenção ou
                  falhas técnicas;</li>
                <li>Conteúdo de sites de terceiros acessados por meio de links disponíveis neste portal;</li>
                <li>Decisões financeiras tomadas com base nas informações disponibilizadas;</li>
                <li>Danos diretos, indiretos, incidentais ou consequentes decorrentes do uso ou da impossibilidade
                  de uso deste site.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">5. Conduta do Usuário</h2>
              <p className="text-gray-700 mb-3">Ao utilizar o {SITE_NAME}, você concorda em:</p>
              <ul className="list-disc list-inside text-gray-700 space-y-2">
                <li>Utilizar o site apenas para fins lícitos e de acordo com estes Termos;</li>
                <li>Não tentar acessar áreas restritas ou sistemas internos do site;</li>
                <li>Não realizar scraping automatizado, sobrecarga de servidores ou qualquer ação que prejudique
                  o funcionamento do site;</li>
                <li>Não reproduzir ou redistribuir o conteúdo do site para fins comerciais sem autorização;</li>
                <li>Respeitar os direitos de propriedade intelectual das fontes originais dos artigos.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">6. Privacidade e Cookies</h2>
              <p className="text-gray-700">
                O uso deste site está sujeito à nossa{' '}
                <a href="/politica-de-privacidade" className="text-blue-600 hover:underline">
                  Política de Privacidade
                </a>
                , que descreve como coletamos, usamos e protegemos seus dados, em conformidade com a
                Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018).
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">7. Publicidade</h2>
              <p className="text-gray-700">
                Este site exibe anúncios do Google AdSense e de outros parceiros de publicidade. Os anúncios
                são selecionados automaticamente com base no conteúdo do site e/ou no histórico de navegação
                do usuário. O {SITE_NAME} não é responsável pelo conteúdo dos anúncios exibidos. Para mais
                informações sobre como o Google utiliza dados para publicidade, consulte a{' '}
                <a
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Política de Privacidade do Google
                </a>
                .
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">8. Lei Aplicável e Foro</h2>
              <p className="text-gray-700">
                Estes Termos de Uso são regidos pela legislação brasileira. Quaisquer disputas decorrentes
                do uso deste site serão submetidas ao foro da Comarca de Blumenau, Estado de Santa Catarina,
                Brasil, com renúncia expressa a qualquer outro, por mais privilegiado que seja.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-3">9. Alterações nos Termos</h2>
              <p className="text-gray-700">
                O {SITE_NAME} reserva-se o direito de modificar estes Termos a qualquer momento. As alterações
                entram em vigor imediatamente após a publicação nesta página, com atualização da data de "Última
                atualização". O uso continuado do site após as alterações constitui aceitação dos novos Termos.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-bold text-gray-900 mb-3">10. Contato e Identificação do Responsável</h2>
              <p className="text-gray-700 mb-4">
                Em caso de dúvidas sobre estes Termos de Uso, entre em contato:
              </p>
              <div className="bg-gray-50 rounded-lg p-4 text-gray-700 text-sm space-y-1">
                <p><strong>Site:</strong> {SITE_NAME}</p>
                <p><strong>Responsável:</strong> {RESPONSIBLE_NAME}</p>
                <p><strong>Localização:</strong> {RESPONSIBLE_LOCATION}</p>
                <p>
                  <strong>E-mail:</strong>{' '}
                  <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 hover:underline">
                    {CONTACT_EMAIL}
                  </a>
                </p>
                <p><strong>URL:</strong> <a href={SITE_URL} className="text-blue-600 hover:underline">{SITE_URL}</a></p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};
